/**
 * Feature Tour Component
 *
 * Guided tour that highlights key UI elements with explanations.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useOverlay } from './OverlayProvider';
import { usePhageStore } from '../../store';

interface TourStep {
  target: string; // CSS selector
  title: string;
  content: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const TOUR_STEPS: TourStep[] = [
  {
    target: '.app-header',
    title: 'Navigation Bar',
    content: 'Access global controls, theme toggle, and view status here.',
    position: 'bottom',
  },
  {
    target: '.app-body',
    title: 'Main Workspace',
    content: 'This area displays the sequence grid, gene maps, or 3D models.',
    position: 'right',
  },
  {
    target: '.key-hint',
    title: 'Keyboard Shortcuts',
    content: 'Look for these hints to learn shortcuts. Speed is key!',
    position: 'top',
  },
  {
    target: '.footer-hints',
    title: 'Quick Reference',
    content: 'Common actions are always visible here.',
    position: 'top',
  },
];

export function FeatureTour(): React.ReactElement | null {
  const { theme } = useTheme();
  const colors = theme.colors;
  const reducedMotion = useReducedMotion();
  const { isOpen, close } = useOverlay();
  const completeTour = usePhageStore((s) => s.completeTour);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  // Reset when closed or reopened
  useEffect(() => {
    if (isOpen('tour')) {
      setStepIndex(0);
    }
  }, [isOpen]);

  const handleFinish = useCallback(() => {
    completeTour('welcome');
    close('tour');
  }, [completeTour, close]);

  const handleNext = useCallback(() => {
    if (stepIndex < TOUR_STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      handleFinish();
    }
  }, [stepIndex, handleFinish]);

  const handlePrev = useCallback(() => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  }, [stepIndex]);

  // Update target rect
  useEffect(() => {
    if (!isOpen('tour')) return;

    const step = TOUR_STEPS[stepIndex];
    const el = document.querySelector(step.target);
    
    if (el) {
      setRect(el.getBoundingClientRect());
      el.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    } else {
      // Skip if target not found
      if (stepIndex < TOUR_STEPS.length - 1) {
        setStepIndex(stepIndex + 1);
      } else {
        handleFinish();
      }
    }
  }, [stepIndex, isOpen, handleFinish, reducedMotion]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen('tour')) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleFinish();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleFinish, handleNext, handlePrev]);

  if (!isOpen('tour') || !rect) return null;

  const step = TOUR_STEPS[stepIndex];

  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  const viewportWidth = vv?.width ?? (typeof window !== 'undefined' ? window.innerWidth : 0);
  const viewportHeight = vv?.height ?? (typeof window !== 'undefined' ? window.innerHeight : 0);
  const viewportLeft = vv?.offsetLeft ?? 0;
  const viewportTop = vv?.offsetTop ?? 0;

  // Calculate position
  const popoverStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 1000,
    width: 'min(300px, calc(100vw - env(safe-area-inset-left) - env(safe-area-inset-right) - 24px))',
    maxWidth: 'calc(100vw - env(safe-area-inset-left) - env(safe-area-inset-right) - 24px)',
    backgroundColor: colors.background,
    border: `2px solid ${colors.accent}`,
    borderRadius: '8px',
    padding: '1rem',
    boxShadow: `0 0 20px ${colors.shadow}`,
  };

  if (step.position === 'bottom') {
    popoverStyle.top = rect.bottom + 16;
    popoverStyle.left = rect.left + (rect.width / 2) - 150;
  } else if (step.position === 'top') {
    popoverStyle.bottom = viewportTop + viewportHeight - rect.top + 16;
    popoverStyle.left = rect.left + (rect.width / 2) - 150;
  } else if (step.position === 'right') {
    popoverStyle.top = rect.top + (rect.height / 2) - 100;
    popoverStyle.left = rect.right + 16;
  } else if (step.position === 'left') {
    popoverStyle.top = rect.top + (rect.height / 2) - 100;
    popoverStyle.right = viewportLeft + viewportWidth - rect.left + 16;
  }

  // Ensure onscreen
  const minX = viewportLeft + 10;
  const maxX = viewportLeft + viewportWidth - 10;
  if (typeof popoverStyle.left === 'number') {
    const leftMax = Math.max(minX, maxX - 300);
    popoverStyle.left = Math.min(Math.max(popoverStyle.left, minX), leftMax);
  }
  if (typeof popoverStyle.right === 'number') {
    popoverStyle.right = Math.min(Math.max(popoverStyle.right, 10), maxX - minX);
  }
  if (typeof popoverStyle.top === 'number') {
    const topMin = viewportTop + 10;
    const topMax = Math.max(topMin, viewportTop + viewportHeight - 10 - 200);
    popoverStyle.top = Math.min(Math.max(popoverStyle.top, topMin), topMax);
  }
  if (typeof popoverStyle.bottom === 'number') {
    const bottomMin = 10;
    const bottomMax = Math.max(bottomMin, viewportHeight - 10 - 200);
    popoverStyle.bottom = Math.min(Math.max(popoverStyle.bottom, bottomMin), bottomMax);
  }

  return (
    <>
      {/* Backdrop highlight mask */}
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 999,
        pointerEvents: 'none',
      }}>
        <div style={{
          position: 'absolute',
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          backgroundColor: 'transparent',
          boxShadow: `0 0 0 9999px rgba(0,0,0,0.7)`,
          borderRadius: '4px',
          border: `2px solid ${colors.accent}`,
          transition: 'all 0.3s ease'
        }} />
      </div>

      {/* Popover */}
      <div style={popoverStyle} className="tour-popover animate-fade-in">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <strong style={{ color: colors.primary }}>{step.title}</strong>
          <span style={{ color: colors.textMuted, fontSize: '0.8rem' }}>
            {stepIndex + 1} / {TOUR_STEPS.length}
          </span>
        </div>
        <p style={{ color: colors.text, marginBottom: '1rem', lineHeight: 1.5 }}>
          {step.content}
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button
            onClick={handleFinish}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Skip
          </button>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handlePrev}
              disabled={stepIndex === 0}
              style={{
                padding: '0.4rem 0.8rem',
                background: 'transparent',
                border: `1px solid ${colors.border}`,
                color: colors.text,
                borderRadius: '4px',
                cursor: stepIndex === 0 ? 'not-allowed' : 'pointer',
                opacity: stepIndex === 0 ? 0.5 : 1
              }}
            >
              Prev
            </button>
            <button
              onClick={handleNext}
              style={{
                padding: '0.4rem 0.8rem',
                background: colors.primary,
                border: 'none',
                color: '#000',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {stepIndex === TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
